// The driver app judges proximity from the rider's LAST pushed coordinate, and iOS assumes a
// 20 s remote cadence: at 68 mph a 60 s-old fix is ~1.8 km behind the car. Push now,
// every 20 s while the page is visible and the session is active, and immediately when the
// rider returns (visibilitychange -> visible, window focus). Hidden pages push nothing.
export const LOCATION_HEARTBEAT_MS = 20000;
// visibilitychange and focus usually fire together on return; one push covers both.
export const WAKE_PUSH_MIN_GAP_MS = 5000;
// An explicit push right after another (same burst) adds no fresher position.
export const REPEAT_PUSH_MIN_GAP_MS = 1000;

// GeolocationPositionError.code: 1 PERMISSION_DENIED, 2 POSITION_UNAVAILABLE, 3 TIMEOUT.
// Only a denial may revoke location access; anything else just skips that beat.
export function isLocationDenied(error) {
  return Boolean(error) && error.code === 1;
}

// Beats in a row without a fix (unavailable, timeout, no code) before the location help shows.
// The permission cookie stays, so the next fix restores the remote on its own.
export const LOCATION_MISSES_BEFORE_HELP = 3;

// error: the beat's GeolocationPositionError, or null for a fix. A fix clears the count; a
// denial leaves it alone because it revokes access on its own path.
export function nextLocationMisses(misses, error) {
  if (error == null) return 0;
  return isLocationDenied(error) ? misses : misses + 1;
}

export const locationHelpDue = misses => misses >= LOCATION_MISSES_BEFORE_HELP;

export function startLocationHeartbeat(push, {
  doc = document, win = window, active: startActive = true, intervalMs = LOCATION_HEARTBEAT_MS,
  wakeGapMs = WAKE_PUSH_MIN_GAP_MS, repeatGapMs = REPEAT_PUSH_MIN_GAP_MS,
} = {}) {
  let timer = null;
  let lastPushAt = -Infinity;
  let stopped = false;
  let active = Boolean(startActive);
  const visible = () => doc.visibilityState !== 'hidden';
  const beating = () => !stopped && active && visible();
  const disarm = () => {
    clearTimeout(timer);
    timer = null;
  };
  // One pending timer per heartbeat; every push restarts the cadence.
  const arm = () => {
    disarm();
    if (beating()) timer = setTimeout(() => send('interval'), intervalMs);
  };
  function send(reason) {
    if (stopped) return;
    lastPushAt = Date.now();
    try {
      push(reason);
    } finally {
      arm();
    }
  }
  function sendUnlessWithin(gapMs, reason) {
    if (Date.now() - lastPushAt >= gapMs) send(reason);
    else if (timer === null) arm();
  }
  function wake(reason) {
    if (beating()) sendUnlessWithin(wakeGapMs, reason);
  }
  const onVisibility = () => (visible() ? wake('visible') : disarm());
  const onFocus = () => wake('focus');
  doc.addEventListener('visibilitychange', onVisibility);
  win.addEventListener('focus', onFocus);
  send('load'); // even for an inactive session, so the rider's coordinates land once
  return {
    // Explicit request (e.g. a lock re-check): skips the wake gap, restarts the cadence.
    // Hidden pages still push nothing; returning pushes anyway.
    pushNow(reason = 'request') {
      if (!stopped && visible()) sendUnlessWithin(repeatGapMs, reason);
    },
    // No cadence while the driver's session is inactive; a session starting re-registers
    // the rider at once (the driver app resets live_pax_coords then).
    setActive(next) {
      if (stopped || Boolean(next) === active) return;
      active = Boolean(next);
      if (!active) disarm();
      else if (visible()) sendUnlessWithin(repeatGapMs, 'resume');
    },
    stop() {
      stopped = true;
      disarm();
      doc.removeEventListener('visibilitychange', onVisibility);
      win.removeEventListener('focus', onFocus);
    },
  };
}
